import { AgencyGetResponseDto, PagedResponse, WorkflowResponseDto } from '@bandido/shared/dtos';
import { AxiosError } from 'axios';
import { useMutation, useQuery } from 'react-query';
import { client } from '@frontend/features/common/services';
import { CaptureCommand, PerformanceConfiguration, PerformanceReport, PerformanceSettings, PerformanceRequest, WorkloadReport } from './types';

export function usePerformanceReport(
  userId: string | undefined,
  agencyId: number,
  workflowId: string,
  range: { from: string; to: string } | null,
  enabled: boolean,
) {
  return useQuery<PerformanceReport, AxiosError>({
    queryKey: ['workflow-performance', userId, agencyId, workflowId, range],
    queryFn: async () =>
      (
        await client.get<PerformanceReport>(`workflow-performance/${agencyId}/${encodeURIComponent(workflowId)}`, {
          params: range,
          timeout: 15000,
        })
      ).data,
    enabled: enabled && Boolean(userId && range),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    cacheTime: 0,
    staleTime: Infinity,
  });
}
export function usePerformanceSettings(agencyId: number, workflowId: string) {
  return useMutation<unknown, AxiosError, PerformanceSettings>(
    (settings) =>
      client.put(`workflow-performance/${agencyId}/${encodeURIComponent(workflowId)}/settings`, settings, {
        timeout: 15000,
      }),
    { retry: false },
  );
}
export function usePerformanceAgency(userId: string | undefined, agencyId: number, enabled: boolean) {
  return useQuery<{ enabled: boolean }, AxiosError>({
    queryKey: ['workflow-performance-agency', userId, agencyId],
    queryFn: async () => (await client.get(`workflow-performance/agencies/${agencyId}`, { timeout: 15000 })).data,
    enabled: enabled && Boolean(userId && agencyId > 0),
    cacheTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
  });
}

export function usePerformanceAgencyMutation(agencyId: number) {
  return useMutation<unknown, AxiosError, { enabled: boolean }>(
    (payload) => client.put(`workflow-performance/agencies/${agencyId}`, payload, { timeout: 15000 }),
    { retry: false },
  );
}

export function usePerformanceConfiguration(
  userId: string | undefined,
  agencyId: number,
  workflowId: string,
  enabled: boolean,
) {
  return useQuery<PerformanceConfiguration, AxiosError>({
    queryKey: ['workflow-performance-config', userId, agencyId, workflowId],
    queryFn: async () =>
      (
        await client.get(`workflow-performance/${agencyId}/${encodeURIComponent(workflowId)}/settings`, {
          timeout: 15000,
        })
      ).data,
    enabled: enabled && Boolean(userId && agencyId > 0 && workflowId),
    cacheTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
    refetchInterval: (data, query) =>
      !query.state.error && data && ['requested', 'observing'].includes(data.capture.state) ? 15000 : false,
    refetchIntervalInBackground: false,
  });
}

export function usePerformanceCapture(agencyId: number, workflowId: string) {
  return useMutation<unknown, AxiosError, CaptureCommand>(
    (payload) =>
      client.put(`workflow-performance/${agencyId}/${encodeURIComponent(workflowId)}/capture`, payload, {
        timeout: 15000,
      }),
    { retry: false },
  );
}

export function performanceError(error: AxiosError | null) {
  switch (error?.response?.status) {
  case 403:
    return 'workflowPerformance.denied';
  case 404:
    return 'workflowPerformance.disabled';
  case 409:
    return 'workflowPerformance.conflict';
  case 429:
    return 'workflowPerformance.busy';
  case 400:
    return 'workflowPerformance.invalid';
  default:
    return 'workflowPerformance.unavailable';
  }
}

/** Metadata pickers only; no implicit report or per-advisor requests. */
export function usePerformanceAgencies(userId: string | undefined, enabled: boolean) {
  return useQuery<AgencyGetResponseDto[], AxiosError>({
    queryKey: ['performance-agencies', userId],
    queryFn: async () => (await client.get('agency/mine', { timeout: 15000 })).data,
    enabled: enabled && Boolean(userId), cacheTime: 0, retry: false,
    refetchOnWindowFocus: true, refetchOnReconnect: false,
  });
}
export function usePerformanceWorkflows(userId: string | undefined, agencyId: number, page: number, enabled: boolean) {
  return useQuery<PagedResponse<WorkflowResponseDto>, AxiosError>({
    queryKey: ['performance-workflows', userId, agencyId, page],
    queryFn: async () => (await client.get(`workflows/agency/${agencyId}`, {
      params: { currentPage: page, pageSize: 100 }, timeout: 15000,
    })).data,
    enabled: enabled && Boolean(userId && agencyId > 0), cacheTime: 0, retry: false,
    refetchOnWindowFocus: true, refetchOnReconnect: false,
  });
}

export function usePerformanceWorkload(
  userId: string | undefined,
  agencyId: number,
  workflowId: string,
  request: PerformanceRequest | null,
  enabled: boolean
) {
  return useQuery<WorkloadReport, AxiosError>({
    queryKey: ['performance-workload', userId, agencyId, workflowId, request],
    queryFn: async () => (await client.get(`workflow-performance/${agencyId}/${encodeURIComponent(workflowId)}/workload`, {
      params: request, timeout: 15000,
    })).data,
    enabled: enabled && Boolean(userId && request), cacheTime: 0, staleTime: Infinity, retry: false,
    refetchOnWindowFocus: false, refetchOnReconnect: false,
  });
}
